// =============================================================================
// DEAL TYPE - Extended Schema with Deal Type System V1
// =============================================================================
// Supports BONUS, DISCOUNT, POINTS, IN_KIND deal types with valuation
// =============================================================================
/* eslint-disable @typescript-eslint/no-explicit-any */

import { normalizeBrandName } from "@/lib/brands/brandNormalization";
import { formatValidityWithFallback } from "@/lib/deals/formatEligibilityRange";
import { getDealValidityLabel, getDealTimeStatus } from "@/lib/deals/dealValidity";
import { toDateOnlyString, parseDateOnlyToLocal } from "@/lib/dates/dateOnly";

// =============================================================================
// ENUMS AND TYPES
// =============================================================================

export type DealCategory = "BONUS" | "DISCOUNT" | "POINTS" | "IN_KIND" | "UNCLASSIFIED";
export type SourceType = "flyer" | "html" | "api" | "pdf" | "email" | "other" | "website" | "terms" | "redemption_portal/terms";
export type FlyerPagePosition = "top" | "middle" | "bottom";
export type AudienceHint = "corporate_bulk" | "personal" | "unknown";
export type BonusType = "CASH_BACK" | "BONUS_CARD";
export type DiscountSubtype = "PAY_X_GET_Y_VALUE" | "PERCENT_OFF_PURCHASE" | "DOLLAR_OFF_PURCHASE";
export type ValuationSource = "SCRAPE" | "MANUAL";
export type RewardItemType = "TICKET" | "PRODUCT" | "SERVICE" | "OTHER";

// CR099: Reward type for semantic deal classification
export type RewardType = "gift_card" | "points" | "cashback" | "in_kind" | "other";

// CR-004: Location variance types
export type AvailabilityScope = "all_locations" | "participating_locations" | "online_only" | "unknown";

// CR-Corporate: Eligibility scope for corporate/member deals
export type EligibilityScope = "PUBLIC" | "CORPORATE" | "MEMBERS_ONLY" | "EMPLOYEES_ONLY" | "UNKNOWN";

// CR292: Date type classification for evergreen/unknown deals
export type DatesType = "FIXED_WINDOW" | "EVERGREEN" | "UNKNOWN";

// CR292: Date mode for ongoing vs fixed window deals (canonical_deal_json.dates.date_mode)
export type DateMode = "ONGOING" | "FIXED";

// Legacy pricing type (for backward compatibility)
export type DealPricingType = "fixed_bonus" | "fixed_discount_amount" | "percent_discount_range";

export interface DiscountTier {
  min: number;
  max: number | null;
  percent: number;
}

export interface InKindTier {
  spend_cad: number;
  reward_qty: number;
  reward_item_type: RewardItemType;
  reward_item_name: string;
}

export interface Deal {
  // Core identification
  merchant: string;
  
  // CR099: Semantic merchant/brand separation
  // promo_merchant = where you spend/buy (e.g., Shoppers, Costco)
  // reward_brand = what you get (e.g., Nintendo eShop GC, Walmart GC)
  promo_merchant_name?: string;       // Required for new deals
  promo_merchant_key?: string;        // Normalized key for comparison
  reward_brand_name?: string | null;  // Only if different from promo merchant
  reward_brand_key?: string | null;   // Normalized key for comparison
  reward_type?: RewardType;           // gift_card, points, cashback, in_kind, other
  reward_type_confidence?: "high" | "medium" | "low"; // CR099 point 3
  qualifying_product_name?: string | null;  // e.g., "Nintendo eShop gift card"
  validation_flags?: string[];        // CR099 validation warnings
  
  // Deal Type System (V1)
  deal_category?: DealCategory;
  headline_mechanic?: string;
  
  // Purchase gift card details (legacy - use promo_merchant_name and reward_brand_name)
  purchase_gift_card_brand: string | null;
  purchase_brand_logo_url: string | null;
  
  // Bonus fields (legacy - use reward_brand_name)
  bonus_merchant: string | null;
  bonus_merchant_logo_url: string | null;
  bonus_amount: number | null;
  spend_amount: number | null;
  bonus_type?: BonusType;
  qualifying_spend_cad?: number | null;
  bonus_value_cad?: number | null;
  
  // Legacy field (kept for compatibility)
  gift_card_brand: string | null;
  bonus_or_discount_amount: number | null;
  
  deal_type: "bonus" | "discount" | "bundle" | "points" | "in_kind" | "unclassified";
  
  // Discount fields
  discount_subtype?: DiscountSubtype;
  pay_amount_cad?: number | null;
  receive_value_cad?: number | null;
  discount_amount_cad?: number | null;
  min_purchase_cad?: number | null;
  
  // Range/percent deal fields
  spend_range_min: number | null;
  spend_range_max: number | null;
  discount_percent: number | null;
  discount_tiers: DiscountTier[] | null;
  deal_pricing_type: DealPricingType | null;
  
  // Points fields - CR229: Enhanced with spend threshold
  points_amount?: number | null;
  points_program_id?: string | null;
  points_program_name?: string | null;
  points_to_currency_rate?: number | null;
  spend_threshold_amount?: number | null;        // CR229: e.g., 100 for "per $100 spent"
  spend_threshold_currency?: string | null;      // CR229: e.g., "CAD"
  points_per_spend?: number | null;              // CR229: Derived ratio e.g., 150 pts per $1
  
  // In-Kind fields
  in_kind_tiers?: InKindTier[] | null;
  
  // Valuation fields
  valuation_source?: ValuationSource;
  unit_value_cad?: number | null;
  valuation_reference_url?: string | null;
  valuation_notes?: string | null;
  
  // Computed values (stored for query performance)
  computed_value_cad?: number | null;
  effective_percent_back?: number | null;
  
  // Validity dates (legacy — prefer dealValidity.ts helper)
  start_date: string | null;
  end_date: string | null;
  bonus_valid_from: string | null;
  bonus_valid_until: string | null;
  purchase_window_start?: string | null;
  purchase_window_end?: string | null;
  redeem_window_start?: string | null;
  redeem_window_end?: string | null;
  
  // CR431: First-class validity metadata
  validity_status?: "known" | "partial" | "unknown" | "inferred_source_window";
  validity_source?: string | null;
  validity_confidence?: number | null;
  validity_evidence?: Record<string, any> | null;
  source_window_from?: string | null;
  source_window_to?: string | null;
  
  // CR292: Date type classification for website/evergreen deals
  dates_type?: DatesType;
  date_phrase?: string | null;
  // CR292: Date mode (ONGOING = evergreen, FIXED = has dates)
  date_mode?: "ONGOING" | "FIXED" | null;

  // Portal/claim details
  requires_physical_card: boolean | null;
  claim_url: string | null;
  claim_channel?: string | null; // CR412: e.g. 'getmybonus'
  // CR-Issuer: Resolved issuer (gift card brand) fields
  issuer_display_name?: string | null;
  issuer_logo_url?: string | null;
  issuer_merchant_key?: string | null;
  // CR416: Server-computed reward presentation
  reward_label?: string | null;
  reward_value_primary?: string | null;
  reward_value_secondary?: string | null;
  reward_source?: string | null;
  // CR417: Server-computed header brand fields
  header_brand_name?: string | null;
  header_brand_source?: string | null;
  header_context_merchant_name?: string | null;
  header_context_merchant_source?: string | null;
  redeem_url: string | null;
  claim_deadline: string | null;
  campaign_name: string | null;
  
  // Fine print
  exclusions?: string | null;
  fine_print: string;
  disclaimer_required?: boolean;
  disclaimer_text?: string | null;
  
  // Source type and provenance
  source_type: SourceType | null;
  source_url?: string | null;
  flyer_deeplink_url?: string | null;
  flyer_page_number?: number | null;
  flyer_page_position?: FlyerPagePosition | null;
  
  // CR064: Separate deal and terms URLs
  deal_url?: string | null;   // Direct link to view/claim the offer
  terms_url?: string | null;  // Link to terms and conditions
  
  // Location/channel
  channel: "in-store" | "online" | "both" | "unknown";
  region: string;
  
  // Proof and verification
  proof_urls: string[];
  confidence: "High" | "Medium" | "Low";
  confidence_reason: string | string[];
  raw_source_notes: string;
  
  // CR013: Evidence transparency fields (admin-only diagnostics)
  evidence_snippet?: string | null;
  decision_reasons?: string[] | null;
  
  // Active status and scoring
  is_active?: boolean;
  normalized_value_score?: number | null;
  
  // CR-004: Location variance fields
  availability_scope?: AvailabilityScope;
  availability_notes?: string | null;
  
  // CR011: Corporate audience classification
  audience_hint?: AudienceHint;
  
  // CR-Corporate: Canonical eligibility scope
  eligibility_scope?: EligibilityScope;
  
  // CR014: Access requirement fields for membership-gated deals
  access_requirement_type?: 'NONE' | 'MEMBERSHIP_REQUIRED' | 'LOGIN_REQUIRED' | 'SIGNUP_REQUIRED' | 'UNKNOWN';
  access_requirement_program_name?: string | null;
  access_requirement_notes?: string | null;
  
  // CR332: Deal artifacts (flyer snippets, etc.)
  artifacts?: DealArtifacts;
}

// CR332: Artifacts attached to a deal
export interface DealArtifacts {
  flyer_snippet?: {
    asset_path: string | null;
    page_number?: number | null;
  };
}

// =============================================================================
// DEAL CATEGORY HELPERS
// =============================================================================
// NOTE: CR043 - Core label/color logic is now in src/lib/deals/dealTypeRegistry.ts
// These functions are re-exported here for backward compatibility
// =============================================================================

import { 
  getDealCategoryLabel as _getDealCategoryLabel,
  getDealCategoryColor as _getDealCategoryColor,
  getDealCategoryIcon,
  CATEGORY_ICONS
} from "@/lib/deals/dealTypeRegistry";

// Re-export for backward compatibility
export const getDealCategoryLabel = _getDealCategoryLabel;
export const getDealCategoryColor = _getDealCategoryColor;
export { getDealCategoryIcon, CATEGORY_ICONS };

/**
 * Infer deal category from deal data if not explicitly set.
 * 
 * NOTE: CR098 - For comprehensive type resolution including canonical_deal_json,
 * use getDealType() from src/lib/deals/getDealType.ts instead.
 * This function is kept for backward compatibility but delegates to getDealType.
 */
export function inferDealCategory(deal: Deal): DealCategory {
  // Import dynamically to avoid circular deps - this is a re-export for compatibility
  // The actual logic is in getDealType.ts
  
  // If explicitly set, use it
  if (deal.deal_category && deal.deal_category !== "UNCLASSIFIED") {
    return deal.deal_category;
  }
  
  // Check deal_type (lowercase) - normalize to uppercase category
  if (deal.deal_type) {
    const upper = deal.deal_type.toUpperCase().replace(/-/g, "_");
    if (["BONUS", "DISCOUNT", "POINTS", "IN_KIND"].includes(upper)) {
      return upper as DealCategory;
    }
  }
  
  // Check for In-Kind deals
  if (deal.in_kind_tiers && deal.in_kind_tiers.length > 0) return "IN_KIND";
  
  // Check for Points deals
  if (deal.points_amount && deal.points_amount > 0) return "POINTS";
  
  // Check for Discount deals
  if (deal.discount_subtype || 
      (deal.pay_amount_cad && deal.receive_value_cad) ||
      deal.discount_amount_cad) {
    return "DISCOUNT";
  }
  
  // Check for Bonus indicators
  if ((deal.spend_amount && deal.bonus_amount) ||
      (deal.qualifying_spend_cad && deal.bonus_value_cad) ||
      deal.bonus_merchant) {
    return "BONUS";
  }
  
  // Default to UNCLASSIFIED (was previously BONUS - more honest now)
  return "UNCLASSIFIED";
}

/**
 * Check if this is a "% back on gift card" BONUS deal (CR061)
 */
export function isBonusPercentBackDeal(deal: Deal): boolean {
  const category = inferDealCategory(deal);
  return category === "BONUS" && 
         deal.discount_percent != null && 
         deal.discount_percent > 0 &&
         (deal.qualifying_spend_cad != null || deal.spend_amount != null);
}

/**
 * Generate headline mechanic from deal data
 */
export function getHeadlineMechanic(deal: Deal): string {
  // If explicitly set, use it
  if (deal.headline_mechanic) return deal.headline_mechanic;
  
  const category = inferDealCategory(deal);
  const spend = deal.spend_amount ?? deal.qualifying_spend_cad ?? deal.pay_amount_cad;
  const bonus = deal.bonus_amount ?? deal.bonus_value_cad ?? deal.bonus_or_discount_amount;
  
  switch (category) {
    case "BONUS": {
      // CR061: Special headline for "% back on gift card" BONUS deals
      if (isBonusPercentBackDeal(deal)) {
        const qualifyingSpend = deal.qualifying_spend_cad ?? deal.spend_amount ?? 0;
        const percent = deal.discount_percent ?? 0;
        const displayBonus = Math.round(qualifyingSpend * (percent / 100));
        return `Spend $${qualifyingSpend.toLocaleString()}, get $${displayBonus.toLocaleString()} in gift cards`;
      }
      
      if (spend && bonus) {
        return `Buy $${spend}, get $${bonus} bonus card`;
      }
      if (bonus) {
        return `Get $${bonus} bonus card`;
      }
      return "Bonus available";
    }
    
    case "DISCOUNT": {
      if (deal.discount_subtype === "PAY_X_GET_Y_VALUE" && deal.pay_amount_cad && deal.receive_value_cad) {
        return `Pay $${deal.pay_amount_cad}, get $${deal.receive_value_cad} value`;
      }
      if (deal.discount_subtype === "PERCENT_OFF_PURCHASE" && deal.discount_percent) {
        return `${deal.discount_percent}% off purchase`;
      }
      if (deal.discount_subtype === "DOLLAR_OFF_PURCHASE" && deal.discount_amount_cad) {
        return `$${deal.discount_amount_cad} off purchase`;
      }
      if (deal.discount_percent) {
        return `${deal.discount_percent}% discount`;
      }
      return "Discount available";
    }
    
    case "POINTS": {
      if (deal.points_amount && deal.points_program_name) {
        return `Earn ${deal.points_amount.toLocaleString()} ${deal.points_program_name}`;
      }
      if (deal.points_amount) {
        return `Earn ${deal.points_amount.toLocaleString()} points`;
      }
      return "Earn bonus points";
    }
    
    case "IN_KIND": {
      const tiers = deal.in_kind_tiers;
      if (tiers && tiers.length > 0) {
        const firstTier = tiers[0];
        // Handle both new format (spend_cad, reward_qty, reward_item_name) and legacy format (spend, item)
        const spend = (firstTier as any).spend_cad ?? (firstTier as any).spend;
        const qty = (firstTier as any).reward_qty ?? 1;
        const itemName = (firstTier as any).reward_item_name ?? (firstTier as any).item ?? "reward";
        return `Spend $${spend}, get ${qty > 1 ? `${qty} ` : ''}${itemName}`;
      }
      return "Free reward available";
    }
    
    default:
      return "Deal available";
  }
}

// =============================================================================
// VALUATION AND COMPUTED VALUES
// =============================================================================

/**
 * Calculate computed value in CAD for a deal
 * Returns null if valuation is not possible
 */
export function computeDealValueCad(deal: Deal): number | null {
  // If already computed, return it
  if (deal.computed_value_cad != null) return deal.computed_value_cad;
  
  const category = inferDealCategory(deal);
  
  switch (category) {
    case "BONUS": {
      // If bonus_value_cad is explicitly set (normalized in Index.tsx), use it
      if (deal.bonus_value_cad != null) return deal.bonus_value_cad;
      if (deal.bonus_amount != null) return deal.bonus_amount;
      
      // Handle bonus_or_discount_amount which may be a percentage or flat amount
      // Two formats exist in legacy data:
      // 1. Decimal format: 0.10 means 10% back
      // 2. Whole number format: 10 means 10% back (when no bonus_amount is set)
      if (deal.bonus_or_discount_amount != null) {
        const spend = deal.qualifying_spend_cad ?? deal.spend_amount;
        
        // Decimal format: 0.10 = 10% back
        if (deal.bonus_or_discount_amount > 0 && deal.bonus_or_discount_amount < 1 && spend) {
          return spend * deal.bonus_or_discount_amount; // 1000 * 0.10 = 100
        }
        
        // Whole number percentage format: 10 = 10% back
        // Heuristic: if the value is <= 100 and spend is large enough,
        // and computing as percentage yields a larger value, it's likely a percentage
        if (deal.bonus_or_discount_amount > 0 && deal.bonus_or_discount_amount <= 100 && spend) {
          const asPercent = deal.bonus_or_discount_amount;
          const computedIfPercent = spend * (asPercent / 100);
          const asDirectDollar = deal.bonus_or_discount_amount;
          
          // If treating as percentage yields a larger, reasonable value, use it
          if (computedIfPercent > asDirectDollar && asPercent <= 50) {
            return computedIfPercent;
          }
        }
        
        // Otherwise it's a flat dollar amount
        return deal.bonus_or_discount_amount;
      }
      return null;
    }
    
    case "DISCOUNT": {
      if (deal.discount_subtype === "PAY_X_GET_Y_VALUE" && deal.pay_amount_cad && deal.receive_value_cad) {
        return deal.receive_value_cad - deal.pay_amount_cad;
      }
      // Handle PERCENT_OFF_PURCHASE - also handle when discount_percent is set without subtype
      if ((deal.discount_subtype === "PERCENT_OFF_PURCHASE" || deal.discount_percent) && deal.discount_percent) {
        const spend = deal.qualifying_spend_cad ?? deal.min_purchase_cad ?? deal.spend_amount;
        if (spend) {
          return spend * (deal.discount_percent / 100);
        }
      }
      if (deal.discount_subtype === "DOLLAR_OFF_PURCHASE" && deal.discount_amount_cad) {
        return deal.discount_amount_cad;
      }
      // Fallback for legacy deals - DON'T use bonus_or_discount_amount for discount deals as it may be a decimal percentage
      return null;
    }
    
    case "POINTS": {
      if (deal.points_amount && deal.points_to_currency_rate) {
        return deal.points_amount * deal.points_to_currency_rate;
      }
      return null; // Can't compute without conversion rate
    }
    
    case "IN_KIND": {
      const tiers = deal.in_kind_tiers;
      if (tiers && tiers.length > 0) {
        const firstTier = tiers[0];
        // Handle both new format (reward_qty * unit_value_cad) and legacy format (value field)
        const legacyValue = (firstTier as any).value;
        if (legacyValue != null) {
          return legacyValue;
        }
        if (deal.unit_value_cad) {
          const qty = (firstTier as any).reward_qty ?? 1;
          return qty * deal.unit_value_cad;
        }
      }
      return null; // Can't compute without unit value
    }
    
    default:
      return null;
  }
}

/**
 * Calculate effective percent back for a deal
 * Returns null if calculation is not possible
 */
export function computeEffectivePercentBack(deal: Deal): number | null {
  // If already computed, return it (handle both decimal and percentage formats)
  if (deal.effective_percent_back != null) {
    // If the value is < 1, it was stored as decimal (0.4330 = 43.30%)
    // If >= 1, it was stored as percentage (43.30)
    if (deal.effective_percent_back < 1 && deal.effective_percent_back > 0) {
      return deal.effective_percent_back * 100;
    }
    return deal.effective_percent_back;
  }
  
  const category = inferDealCategory(deal);
  const computedValue = computeDealValueCad(deal);
  
  if (computedValue == null) return null;
  
  switch (category) {
    case "BONUS": {
      const spend = deal.qualifying_spend_cad ?? deal.spend_amount;
      if (spend && spend > 0) {
        return (computedValue / spend) * 100;
      }
      return null;
    }
    
    case "DISCOUNT": {
      // For percent off deals, return the discount_percent directly
      if (deal.discount_percent) {
        return deal.discount_percent;
      }
      if (deal.discount_subtype === "PAY_X_GET_Y_VALUE" && deal.pay_amount_cad) {
        return (computedValue / deal.pay_amount_cad) * 100;
      }
      const spend = deal.min_purchase_cad ?? deal.qualifying_spend_cad ?? deal.spend_amount;
      if (spend && spend > 0) {
        return (computedValue / spend) * 100;
      }
      return null;
    }
    
    case "POINTS": {
      const spend = deal.qualifying_spend_cad ?? deal.spend_amount;
      if (spend && spend > 0) {
        return (computedValue / spend) * 100;
      }
      return null;
    }
    
    case "IN_KIND": {
      const tiers = deal.in_kind_tiers;
      if (tiers && tiers.length > 0) {
        const firstTier = tiers[0];
        // Handle both new format (spend_cad) and legacy format (spend)
        const spend = (firstTier as any).spend_cad ?? (firstTier as any).spend;
        if (spend && spend > 0) {
          return (computedValue / spend) * 100;
        }
      }
      return null;
    }
    
    default:
      return null;
  }
}

/**
 * Check if deal has valuation available
 */
export function hasValuation(deal: Deal): boolean {
  return computeDealValueCad(deal) != null;
}

// =============================================================================
// DERIVED PROPERTIES (Legacy support)
// =============================================================================

/**
 * Check if the bonus is usable at the same place as what you're purchasing.
 */
export function isSameMerchantBonus(deal: Deal): boolean {
  if (!deal.bonus_merchant) {
    return true;
  }
  
  const purchaseEntity = deal.purchase_gift_card_brand ?? deal.gift_card_brand ?? deal.merchant;
  
  const normalizedBonus = normalizeBrandName(deal.bonus_merchant);
  const normalizedPurchase = normalizeBrandName(purchaseEntity);
  
  return normalizedBonus === normalizedPurchase;
}

/**
 * Check if this is a bulk deal (spend > $500)
 */
export function isBulkDeal(deal: Deal): boolean {
  const spend = deal.spend_amount ?? deal.spend_range_min ?? 0;
  return spend > 500;
}

/**
 * Check if this is a range/percent discount deal
 */
export function isRangePercentDeal(deal: Deal): boolean {
  return deal.deal_pricing_type === "percent_discount_range" || 
         (Array.isArray(deal.discount_tiers) && deal.discount_tiers.length > 0) ||
         (deal.discount_percent != null && deal.spend_range_min != null);
}

// =============================================================================
// HELPER FUNCTIONS (Legacy + Enhanced)
// =============================================================================

/**
 * Calculate effective value percentage for a deal (Legacy + new logic)
 */
export function calculateEffectiveValue(deal: Deal): number {
  // Try new computation first
  const newPercent = computeEffectivePercentBack(deal);
  if (newPercent != null) return newPercent;
  
  // Legacy fallback for range-based percent deals
  if (isRangePercentDeal(deal) && deal.discount_percent) {
    return deal.discount_percent;
  }
  
  const spend = deal.spend_amount ?? 0;
  const bonus = deal.bonus_amount ?? deal.bonus_or_discount_amount ?? 0;
  
  if (spend > 0 && bonus > 0) {
    if (bonus > 0 && bonus < 1) {
      return bonus * 100;
    }
    
    const ratio = (bonus / spend) * 100;
    if (bonus <= 50 && spend >= 500 && ratio < 5) {
      return bonus;
    }
    
    return ratio;
  }
  return 0;
}

/**
 * Check if deal bonus is stored as a percentage
 */
export function getBonusPercentageValue(deal: Deal): number | null {
  const spend = deal.spend_amount ?? 0;
  const bonus = deal.bonus_amount ?? deal.bonus_or_discount_amount ?? 0;
  
  if (spend > 0 && bonus > 0) {
    if (bonus > 0 && bonus < 1) {
      return bonus * 100;
    }
    
    const ratio = (bonus / spend) * 100;
    if (bonus <= 50 && spend >= 500 && ratio < 5) {
      return bonus;
    }
  }
  return null;
}

export function isBonusPercentage(deal: Deal): boolean {
  return getBonusPercentageValue(deal) !== null;
}

/**
 * Generate deal summary text
 */
export function getDealSummary(deal: Deal): string {
  // Use headline mechanic if available
  const mechanic = getHeadlineMechanic(deal);
  if (mechanic && mechanic !== "Deal available") return mechanic;
  
  const spend = deal.spend_amount;
  const bonus = deal.bonus_amount ?? deal.bonus_or_discount_amount;
  const purchaseBrand = deal.purchase_gift_card_brand ?? deal.gift_card_brand;
  const bonusMerchant = deal.bonus_merchant;
  
  if (isRangePercentDeal(deal) && deal.discount_percent) {
    if (deal.spend_range_min && deal.spend_range_max) {
      return `Spend $${deal.spend_range_min.toLocaleString()}–$${deal.spend_range_max.toLocaleString()}: ${deal.discount_percent}% discount`;
    }
    if (deal.spend_range_min) {
      return `Spend $${deal.spend_range_min.toLocaleString()}+: ${deal.discount_percent}% discount`;
    }
    return `${deal.discount_percent}% discount on gift cards`;
  }
  
  if (purchaseBrand && spend && bonus && bonusMerchant) {
    return `Buy $${spend} ${purchaseBrand} → Get $${bonus} ${bonusMerchant} eGift bonus.`;
  }
  
  if (purchaseBrand && spend && bonus) {
    return `Buy $${spend} ${purchaseBrand} gift card, get $${bonus} ${deal.deal_type}`;
  }
  
  if (spend && bonus) {
    return `Spend $${spend} on gift cards, get $${bonus} ${deal.deal_type}`;
  }
  
  if (bonus && bonusMerchant) {
    return `Bonus available (amount varies) at ${bonusMerchant}`;
  }
  
  return "Gift card deal available";
}

/**
 * Format discount tiers for display
 */
export function formatDiscountTiers(deal: Deal): string[] {
  if (!deal.discount_tiers || deal.discount_tiers.length === 0) {
    return [];
  }
  
  return deal.discount_tiers.map(tier => {
    if (tier.max) {
      return `Purchase $${tier.min.toLocaleString()}–$${tier.max.toLocaleString()} per order: ${tier.percent}% discount`;
    }
    return `Purchase $${tier.min.toLocaleString()}+ per order: ${tier.percent}% discount`;
  });
}

/**
 * Format In-Kind tiers for display
 */
export function formatInKindTiers(deal: Deal): string[] {
  if (!deal.in_kind_tiers || deal.in_kind_tiers.length === 0) {
    return [];
  }
  
  return deal.in_kind_tiers.map(tier => {
    // Handle both new format (spend_cad, reward_qty, reward_item_name) 
    // and legacy format (spend, item, value)
    const spend = (tier as any).spend_cad ?? (tier as any).spend;
    const qty = (tier as any).reward_qty ?? 1;
    const itemName = (tier as any).reward_item_name ?? (tier as any).item ?? "reward";
    
    if (spend != null && itemName) {
      return `Spend $${spend} → Get ${qty > 1 ? `${qty} ` : ''}${itemName}`;
    }
    return `${itemName}`;
  });
}

/**
 * Format validity dates
 * CR209: Updated to use shared formatters with proper date rendering
 * CR282: Uses formatEligibilityRange for user-proof formatting (no Jan 28–3 compression)
 */
export function formatValidity(deal: Deal): string {
  // CR431: Delegate to unified dealValidity helper
  return getDealValidityLabel(deal as any);
}

/**
 * Format bonus card validity
 */
export function formatBonusValidity(deal: Deal): string | null {
  if (deal.bonus_valid_from && deal.bonus_valid_until) {
    return `Bonus card valid: ${deal.bonus_valid_from} to ${deal.bonus_valid_until}`;
  }
  if (deal.bonus_valid_from) {
    return `Bonus card valid from: ${deal.bonus_valid_from}`;
  }
  if (deal.bonus_valid_until) {
    return `Bonus card must be used by: ${deal.bonus_valid_until}`;
  }
  return null;
}

/**
 * Format where found text based on source type
 */
export function formatWhereFound(deal: Deal): string {
  const region = deal.region || "Unknown";
  const channel = deal.channel !== "unknown" ? ` (${deal.channel})` : "";
  
  if (deal.source_type === "redemption_portal/terms") {
    return "Website/Terms (getmybonus.ca)";
  }
  
  if (deal.source_type === "website" || deal.source_type === "terms") {
    return `Website/Terms${channel}`;
  }
  
  if (deal.source_type === "flyer") {
    return `${region} flyer${channel}`;
  }
  
  return `${region}${channel}`;
}

/**
 * Compute deal status for sorting
 */
export function computeDealStatusForSort(deal: Deal): number {
  // CR432: Delegate to unified helper for timezone-safe, unknown-aware sorting
  const status = getDealTimeStatus(deal as any);
  switch (status) {
    case "expired": return 2;
    case "upcoming": return 1;
    case "unknown": return 3; // Unknown sorts after expired
    case "active": 
    default: return 0;
  }
}

/**
 * Sort deals by: status (active -> upcoming -> expired), then normalized_value_score (desc),
 * then effective value (desc), then merchant name (asc)
 */
export function sortDeals(deals: Deal[]): Deal[] {
  return [...deals].sort((a, b) => {
    // 1. Status order: active (0) -> upcoming (1) -> expired (2)
    const statusA = computeDealStatusForSort(a);
    const statusB = computeDealStatusForSort(b);
    if (statusA !== statusB) return statusA - statusB;
    
    // 2. Normalized value score (descending) - nulls last
    const scoreA = a.normalized_value_score ?? -Infinity;
    const scoreB = b.normalized_value_score ?? -Infinity;
    if (scoreB !== scoreA) return scoreB - scoreA;
    
    // 3. Fallback to effective percent back (descending)
    const percentA = calculateEffectiveValue(a);
    const percentB = calculateEffectiveValue(b);
    if (percentB !== percentA) return percentB - percentA;
    
    // 4. Dollar value as tie-breaker (descending)
    const valueA = computeDealValueCad(a);
    const valueB = computeDealValueCad(b);
    if (valueA != null && valueB != null && valueA !== valueB) {
      return valueB - valueA;
    }
    
    // 5. Merchant name alphabetically (ascending)
    const merchantA = a.merchant.toLowerCase();
    const merchantB = b.merchant.toLowerCase();
    if (merchantA !== merchantB) return merchantA.localeCompare(merchantB);
    
    // 6. Purchase brand alphabetically (ascending) for stability
    const brandA = (a.purchase_gift_card_brand ?? a.gift_card_brand ?? "").toLowerCase();
    const brandB = (b.purchase_gift_card_brand ?? b.gift_card_brand ?? "").toLowerCase();
    return brandA.localeCompare(brandB);
  });
}

/**
 * Check if deal has incomplete brand/amount info
 */
export function hasMissingDetails(deal: Deal): boolean {
  const purchaseBrand = deal.purchase_gift_card_brand ?? deal.gift_card_brand;
  const bonus = deal.bonus_amount ?? deal.bonus_or_discount_amount;
  
  if (isRangePercentDeal(deal) && deal.discount_percent) {
    return !purchaseBrand || purchaseBrand === "Varies";
  }
  
  return !purchaseBrand || purchaseBrand === "Varies" || !bonus;
}

/**
 * CR292: Check if deal has missing validity dates
 * 
 * Returns false (no warning) if:
 * - dates_type is EVERGREEN (standing offers don't need dates)
 * - dates_type is UNKNOWN with a date_phrase (captured raw text instead)
 * 
 * Returns true (show warning) only if:
 * - dates_type is FIXED_WINDOW and dates are missing (extraction failure)
 * - dates_type is undefined/null and dates are missing (legacy check)
 */
export function hasMissingDates(deal: Deal): boolean {
  // CR292: Suppress warning for EVERGREEN deals - they have no time window
  if (deal.dates_type === "EVERGREEN") {
    return false;
  }
  
  // CR292: Suppress warning for UNKNOWN with date_phrase - we have the raw text
  if (deal.dates_type === "UNKNOWN" && deal.date_phrase) {
    return false;
  }
  
  // Standard check: missing if no start AND no end date
  return !deal.start_date && !deal.end_date;
}

/**
 * Check if deal needs "Confirm on site" message (In-Kind without valuation)
 */
export function needsConfirmOnSite(deal: Deal): boolean {
  const category = inferDealCategory(deal);
  
  if (category === "IN_KIND") {
    return !hasValuation(deal);
  }
  
  if (category === "POINTS") {
    return !deal.points_to_currency_rate;
  }
  
  return false;
}

// =============================================================================
// DISCOUNT FORMATTING UTILITIES
// =============================================================================

/**
 * Format discount percent for display
 * - Integer values: show no decimals (e.g., 10%)
 * - Decimal values: show 1 decimal place (e.g., 7.5%)
 */
export function formatDiscountPercent(percent: number): string {
  if (Number.isInteger(percent)) {
    return `${percent}%`;
  }
  return `${percent.toFixed(1)}%`;
}

/**
 * Format spend/qualifying amount for display
 * - Whole dollar amounts: no decimals (e.g., $50,000)
 * - Fractional amounts: 2 decimals (e.g., $50.25)
 */
export function formatSpendAmount(amount: number): string {
  if (Number.isInteger(amount)) {
    return `$${amount.toLocaleString()}`;
  }
  return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Format discount/savings value for display
 * - If >= $10: round to nearest dollar, no cents (e.g., $50)
 * - If < $10: show 2 decimals (e.g., $7.50)
 */
export function formatDiscountValue(value: number): string {
  if (value >= 10) {
    return `$${Math.round(value).toLocaleString()}`;
  }
  return `$${value.toFixed(2)}`;
}

/**
 * Compute discount dollar value from percent and qualifying spend
 */
export function computeDiscountDollarValue(deal: Deal): number | null {
  const category = inferDealCategory(deal);
  if (category !== "DISCOUNT") return null;
  
  // PAY_X_GET_Y_VALUE: difference between receive and pay
  if (deal.discount_subtype === "PAY_X_GET_Y_VALUE" && deal.pay_amount_cad && deal.receive_value_cad) {
    return deal.receive_value_cad - deal.pay_amount_cad;
  }
  
  // PERCENT_OFF_PURCHASE: compute from percent and qualifying spend
  if (deal.discount_subtype === "PERCENT_OFF_PURCHASE" && deal.discount_percent) {
    const spend = deal.qualifying_spend_cad ?? deal.min_purchase_cad ?? deal.spend_amount;
    if (spend) {
      return spend * (deal.discount_percent / 100);
    }
  }
  
  // DOLLAR_OFF_PURCHASE: direct value
  if (deal.discount_subtype === "DOLLAR_OFF_PURCHASE" && deal.discount_amount_cad) {
    return deal.discount_amount_cad;
  }
  
  // Fallback: use computed_value_cad if available
  return deal.computed_value_cad ?? null;
}

/**
 * Get discount deal display strings
 * Returns primary headline and secondary metrics line
 * 
 * CR059 Rule:
 * - If spend is KNOWN (qualifying_spend_cad, min_purchase_cad, or spend_amount) → dollar headline
 * - If spend is UNKNOWN (variable spend) → percent headline
 */
export function getDiscountDisplayStrings(deal: Deal): { 
  headline: string; 
  metrics: string | null;
  headlineMode: "DOLLAR_ON_SPEND" | "PERCENT_VARIABLE" | "FIXED_VALUE" | "DEFAULT";
} {
  const category = inferDealCategory(deal);
  if (category !== "DISCOUNT") {
    return { headline: getHeadlineMechanic(deal), metrics: null, headlineMode: "DEFAULT" };
  }
  
  const dollarValue = computeDiscountDollarValue(deal);
  const percent = deal.discount_percent;
  const spend = deal.qualifying_spend_cad ?? deal.min_purchase_cad ?? deal.spend_amount;
  
  let headline = "";
  let metrics: string | null = null;
  let headlineMode: "DOLLAR_ON_SPEND" | "PERCENT_VARIABLE" | "FIXED_VALUE" | "DEFAULT" = "DEFAULT";
  
  // CR059: Determine headline based on whether spend is known
  const isSpendKnown = spend != null && spend > 0;
  const hasPercent = percent != null && percent > 0;
  
  // Build headline based on subtype and spend availability
  if (deal.discount_subtype === "PAY_X_GET_Y_VALUE" && deal.pay_amount_cad && deal.receive_value_cad) {
    headline = `Pay ${formatSpendAmount(deal.pay_amount_cad)}, get ${formatSpendAmount(deal.receive_value_cad)} value`;
    headlineMode = "DOLLAR_ON_SPEND";
  } else if (isSpendKnown && hasPercent) {
    // CR059: Spend is known → dollar headline "Get $X off when spending $Y"
    const dollarOff = dollarValue ?? (spend * (percent / 100));
    headline = `Get ${formatDiscountValue(dollarOff)} off when spending ${formatSpendAmount(spend)}`;
    headlineMode = "DOLLAR_ON_SPEND";
  } else if (hasPercent && !isSpendKnown) {
    // CR059: Variable spend → percent headline "Get X% back in gift cards"
    const brandSuffix = deal.gift_card_brand ? ` in ${deal.gift_card_brand} gift cards` : " in gift cards";
    headline = `Get ${formatDiscountPercent(percent)} back${brandSuffix}`;
    headlineMode = "PERCENT_VARIABLE";
  } else if (deal.discount_subtype === "DOLLAR_OFF_PURCHASE" && deal.discount_amount_cad) {
    if (spend) {
      headline = `Get ${formatDiscountValue(deal.discount_amount_cad)} off when spending ${formatSpendAmount(spend)}`;
      headlineMode = "DOLLAR_ON_SPEND";
    } else {
      headline = `Get ${formatDiscountValue(deal.discount_amount_cad)} off`;
      headlineMode = "FIXED_VALUE";
    }
  } else if (hasPercent) {
    // Fallback for percent without spend - keep percent-based
    headline = `${formatDiscountPercent(percent)} off`;
    headlineMode = "PERCENT_VARIABLE";
  } else {
    // Fallback to generic headline
    headline = deal.headline_mechanic || "Discount available";
  }
  
  // Build metrics line - CR059: Only show dollar value if spend is known
  if (isSpendKnown && percent != null && dollarValue != null) {
    metrics = `≈ ${formatDiscountPercent(percent)} off • ≈ ${formatDiscountValue(dollarValue)} value`;
  } else if (percent != null && !isSpendKnown) {
    // Variable spend: only show percent, no dollar value
    metrics = `≈ ${formatDiscountPercent(percent)} back`;
  } else if (percent != null) {
    metrics = `≈ ${formatDiscountPercent(percent)} off`;
  } else if (dollarValue != null) {
    metrics = `≈ ${formatDiscountValue(dollarValue)} value`;
  }
  
  return { headline, metrics, headlineMode };
}
